<div class="flex flex-col">
    @if (isCurrentDeactivated()) {
        <div class="bg-secondary-orange-light text-secondary-orange-02 rounded px-3 py-2 mb-3 text-sm font-semibold">
            {{ literalService.get('services.routes.deactivatedBanner', true) }}
        </div>
    }
    <!-- Row 1: Code selector or input -->
    <div class="flex flex-col">
        @if (!isCreating) {
            <div class="flex items-center mb-[6px]">
                <label class="font-normal text-base text-primary-purple">{{ literalService.get('services.routes.promotionalCode.select', true) }}</label>
            </div>
            <select2
                class="block w-full"
                [placeholder]="literalService.get('services.routes.promotionalCode.selectPlaceholder', true)"
                [data]="getCodesForSelect()"
                [value]="editingCode.id"
                [templates]="{option: codeOption}"
                (update)="selectCode($event)"
            >
                <ng-template #codeOption let-label="label">
                    <app-select2-option [label]="label" />
                </ng-template>
            </select2>
        } @else {
            <div class="flex items-center mb-[6px]">
                <label class="font-normal text-base text-primary-purple">{{ literalService.get('services.routes.promotionalCode.code', true) }}</label>
            </div>
            <input
                type="text"
                class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                [value]="editingCode.code || ''"
                (change)="updateCode($event)"
                [placeholder]="literalService.get('services.routes.promotionalCode.codePlaceholder', true)"
            />
        }
    </div>

    <!-- Code rename (only in editing mode) -->
    @if (isEditing) {
        <div class="flex flex-col mt-4">
            <label class="font-normal text-base text-primary-purple mb-[6px]">{{ literalService.get('services.routes.promotionalCode.code', true) }}</label>
            <input
                type="text"
                class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                [value]="editingCode.code || ''"
                (change)="updateCode($event)"
                [placeholder]="literalService.get('services.routes.promotionalCode.codePlaceholder', true)"
            />
        </div>
    }

    <!-- Discount Type + Discount Amount -->
    @if (editingCode.id || isCreating) {
        <div class="flex gap-4 mt-4">
            <div class="flex flex-col flex-1">
                <label class="font-normal text-base text-primary-purple mb-[6px]">{{ literalService.get('services.routes.promotionalCode.discountType', true) }}</label>
                @if (isCreating || isEditing) {
                    <select2
                        class="block w-full"
                        [placeholder]="literalService.get('services.routes.promotionalCode.selectTypePlaceholder', true)"
                        [data]="getDiscountTypes()"
                        [value]="editingCode.discountType"
                        [templates]="{option: discountTypeOption}"
                        (update)="changeDiscountType($event)"
                    >
                        <ng-template #discountTypeOption let-label="label">
                            <app-select2-option [label]="label" />
                        </ng-template>
                    </select2>
                } @else {
                    <input
                        type="text"
                        class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                        [value]="literalService.get('promotionalCodeDiscountType.' + editingCode.discountType, true)"
                        disabled
                    />
                }
            </div>
            <div class="flex flex-col flex-1">
                <label class="font-normal text-base text-primary-purple mb-[6px]">
                    {{ literalService.get(editingCode.discountType === PromotionalCodeDiscountType.RELATIVE ? 'services.routes.promotionalCode.discountValuePercent' : 'services.routes.promotionalCode.discountValueAbsolute', true) }}
                </label>
                <input
                    type="number"
                    step="0.01"
                    class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                    [value]="editingCode.discountValue"
                    [disabled]="!isCreating && !isEditing"
                    (change)="updateDiscountValue($event)"
                    [placeholder]="literalService.get('services.routes.promotionalCode.discountValuePlaceholder', true)"
                />
            </div>
        </div>
    }

    <!-- Max Usages + Current Usages -->
    @if (editingCode.id || isCreating) {
        <div class="flex gap-4 mt-4">
            <div class="flex flex-col flex-1">
                <label class="font-normal text-base text-primary-purple mb-[6px]">{{ literalService.get('services.routes.promotionalCode.maxUsages', true) }}</label>
                <input
                    type="number"
                    min="1"
                    class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                    [value]="editingCode.maxUsages ?? ''"
                    [disabled]="!isCreating && !isEditing"
                    (change)="updateMaxUsages($event)"
                    [placeholder]="literalService.get('services.routes.promotionalCode.maxUsagesPlaceholder', true)"
                />
            </div>
            <div class="flex flex-col flex-1">
                @if (editingCode.id && !isCreating) {
                    <label class="font-normal text-base text-primary-purple mb-[6px]">{{ literalService.get('services.routes.promotionalCode.currentUsages', true) }}</label>
                    <input
                        type="text"
                        class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                        [value]="editingCode.currentUsages ?? 0"
                        disabled
                    />
                }
            </div>
        </div>
    }

    <!-- Valid From + Valid Until -->
    @if (editingCode.id || isCreating) {
        <div class="flex gap-4 mt-4">
            <div class="flex flex-col flex-1">
                <label class="font-normal text-base text-primary-purple mb-[6px]">{{ literalService.get('services.routes.promotionalCode.validFrom', true) }}</label>
                <input
                    type="datetime-local"
                    class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                    [value]="toDatetimeLocal(editingCode.validFrom)"
                    [disabled]="!isCreating && !isEditing"
                    (change)="updateValidFrom($event)"
                />
            </div>
            <div class="flex flex-col flex-1">
                <label class="font-normal text-base text-primary-purple mb-[6px]">{{ literalService.get('services.routes.promotionalCode.validUntil', true) }}</label>
                <input
                    type="datetime-local"
                    class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                    [value]="toDatetimeLocal(editingCode.validUntil)"
                    [disabled]="!isCreating && !isEditing"
                    (change)="updateValidUntil($event)"
                />
            </div>
        </div>
    }

    <!-- Footer Buttons -->
    @if (!hideFooter) {
        <div class="flex items-center mt-8" [ngClass]="{'justify-center': isCreating || isEditing, 'justify-end': !isCreating && !isEditing}">
            @if (isCreating || isEditing) {
                <button type="button" class="secondary mr-5 gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer" (click)="cancelEdit()">
                    {{ literalService.get('cancel', true) }}
                </button>
                <button type="button" class="primary gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer text-white" [disabled]="!isFormValid()" (click)="onSubmit()">
                    {{ literalService.get('save', true) }}
                </button>
            } @else {
                <button type="button" class="primary gap-4 px-6 py-2 border-2 font-semibold uppercase cursor-pointer text-white" (click)="startCreating()">
                    {{ literalService.get('services.routes.promotionalCode.new', true) }}
                </button>
            }
        </div>
    }
</div>
